/**
 * ChatBrowserSessionWidget
 *
 * Inline chat widget that displays a live browser screencast for a given execution.
 * Uses the same OutputCard + PreviewLayover pattern as the Run Log for consistency.
 *
 * Lifecycle:
 * - Polls execution outputs for a browser display URL
 * - Shows OutputCard with BrowserRenderer inline (same as Run Log)
 * - Click Expand button to open PreviewLayover with BrowserLayout (same as Run Log)
 */

import { useMemo, useState } from 'react';

import { useGetExecutionOutputs } from '@/generated/hooks/jarvis/v1/api';
import type { V1CompletedRunStateOutputs } from '@/generated/hooks/bishop/models';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { useAgentStream } from '@/modules/browser-automation';
import { resolveAgentStreamUrl } from '@/shared/utils/browser-urls';
import { OutputCard } from '@/modules/outputs/components/OutputCard';
import { PreviewLayover } from '@/modules/outputs/components/PreviewLayover';
import { EOutputCardSize, type TResolvedValue } from '@/modules/outputs/types';
import {
  detectBrowserSession,
  extractOutputsValue,
} from '@/modules/outputs/utils';

interface IChatBrowserSessionWidgetProps {
  executionId: string;
  /** Output values from the completed execution, for immediate browser session detection */
  outputValues?: Record<string, unknown>;
}

export function ChatBrowserSessionWidget({
  executionId,
  outputValues,
}: IChatBrowserSessionWidgetProps) {
  'use no memo';

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const [previewData, setPreviewData] = useState<{
    name: string;
    value: TResolvedValue;
  } | null>(null);

  // Try to detect browser session directly from output values in the artifact.
  const browserSessionFromOutputValues = useMemo(() => {
    if (!outputValues) return null;
    const resolved = extractOutputsValue(
      outputValues as V1CompletedRunStateOutputs,
    );
    return detectBrowserSession(resolved);
  }, [outputValues]);

  // Fallback: poll for execution outputs to find browser display URL.
  // Only poll if we didn't already detect a session from outputValues.
  const { data: outputsResponse } = useGetExecutionOutputs(
    currentOrganizationId,
    currentWorkspaceId,
    executionId,
    {
      query: {
        enabled: Boolean(
          !browserSessionFromOutputValues &&
            executionId &&
            currentOrganizationId &&
            currentWorkspaceId,
        ),
        refetchInterval: query => {
          // Stop polling once retries are exhausted, otherwise a backend 500
          // here would be hit every 2s forever.
          if (query.state.error) return false;
          const outputs = query.state.data?.data?.outputs as
            | V1CompletedRunStateOutputs
            | undefined;
          if (!outputs) return 2000;
          const resolved = extractOutputsValue(outputs);
          if (detectBrowserSession(resolved)) return false;
          if (Object.keys(outputs).length > 0) return false;
          return 2000;
        },
        staleTime: 30 * 1000,
      },
    },
  );

  const browserSessionFromPolling = useMemo(() => {
    const outputs = outputsResponse?.data?.outputs as
      | V1CompletedRunStateOutputs
      | undefined;
    if (!outputs) return null;
    return detectBrowserSession(extractOutputsValue(outputs));
  }, [outputsResponse]);

  const browserSession =
    browserSessionFromOutputValues ?? browserSessionFromPolling;

  // Construct browser instance value for BrowserRenderer
  const browserValue = useMemo(() => {
    if (!browserSession) return null;
    return {
      browser_name: browserSession.identifier,
      live_view_url: browserSession.liveViewUrl,
    };
  }, [browserSession]);

  // Pre-cache agent steps so BrowserStepsViewer has data when expanded
  const agentStreamUrl = useMemo(() => {
    if (
      !browserSession?.liveViewUrl ||
      !currentOrganizationId ||
      !currentWorkspaceId
    )
      return null;
    return resolveAgentStreamUrl(
      browserSession.liveViewUrl,
      currentOrganizationId,
      currentWorkspaceId,
    );
  }, [browserSession, currentOrganizationId, currentWorkspaceId]);

  // Suspend pre-cache when expanded: the BrowserStepsViewer in the expanded
  // view has its own useAgentStream connection. Running both simultaneously
  // wastes an HTTP connection slot (Chrome allows only 6 per origin over
  // HTTP/1.1). Steps are preserved in streamCache (localStorage) across
  // unmount/remount.
  useAgentStream(previewData ? null : agentStreamUrl);

  if (!browserValue) {
    return null;
  }

  const outputName = browserSession?.identifier ?? 'Browser';

  return (
    <>
      {/* Unmount OutputCard when expanded: it's invisible behind the
          full-screen PreviewLayover portal and its ScreencastViewer holds
          an SSE connection we don't need while the expanded view has its own.
          On close, OutputCard remounts and restores the last frame from
          frameCache (localStorage) before reconnecting SSE. */}
      {!previewData && (
        <div className="w-full">
          <OutputCard
            name={outputName}
            value={browserValue}
            size={EOutputCardSize.LG}
            onExpand={(name, value) => setPreviewData({ name, value })}
          />
        </div>
      )}
      {previewData && (
        <PreviewLayover
          isOpen
          outputName={previewData.name}
          outputValue={previewData.value}
          onClose={() => setPreviewData(null)}
        />
      )}
    </>
  );
}
